<template>
    <div>
        <template v-if="relationList">
            <h4>
                {{ title }}
            </h4>
            <!-- Checkbox from ThemeLayerDetail.vue -->
            <slot></slot>
            <template v-if="relationList && selectableLayers">
                <v-draggable :list="relationList" handle=".fa-arrows-alt">
                    <div
                        v-for="(relation, index) in relationList"
                        :key="`relation_${index}`"
                        class="accordion"
                        role="tablist"
                    >
                        <b-card
                            no-body
                            class="mb-1"
                            v-if="relation.mode !== 'delete'"
                        >
                            <b-card-header
                                header-tag="header"
                                :class="{
                                    'p-1': true,
                                    collapsed: !accordionState[index],
                                    'not-collapsed': accordionState[index]
                                }"
                                role="tab"
                                @click="$_selectRelation(index)"
                                data-toggle="collapse"
                            >
                                <em class="fa fa-arrows-alt"></em>
                                <span>{{ relation.name.toUpperCase() }}</span>
                                <div class="float-right">
                                    <a
                                        :title="$t('relations.delete')"
                                        class="pr-4"
                                        @click.stop.prevent="
                                            $_onRemoveRelation(index)
                                        "
                                    >
                                        <em class="fas fa-trash-alt fa-sm"></em>
                                    </a>
                                    <em class="fa iconCollapseExpand"></em>
                                </div>
                            </b-card-header>
                            <b-collapse
                                :id="`accordion-${index}`"
                                :accordion="`relations-accordion-${id}`"
                                v-model="accordionState[index]"
                                role="tabpanel"
                            >
                                <b-card-body
                                    v-if="accordionState[index] === true"
                                >
                                    <!--existing relation-->
                                    <themelayer-relation
                                        :relationshipType="relationshipType"
                                        :targetLabel="targetLabel"
                                        :relationList="relationList"
                                        :relation="relation"
                                        :selectableLayers="selectableLayers"
                                        :sourceLayerId="layerId"
                                        :edit="true"
                                        @update="$_updateRelationData($event)"
                                    />
                                </b-card-body>
                            </b-collapse>
                        </b-card>
                    </div>
                </v-draggable>
            </template>
            <b-card no-body v-if="addRelationLayerId && selectableLayers">
                <b-card-body>
                    <!--new relation-->
                    <themelayer-relation
                        :relationshipType="relationshipType"
                        :targetLabel="targetLabel"
                        :relationList="relationList"
                        :relation="{
                            targetThemeLayerId: addRelationLayerId
                        }"
                        :selectableLayers="selectableLayers"
                        :tableNames="tableNames"
                        :sourceLayerId="layerId"
                        @update="$_updateRelationData($event)"
                        @cancel="
                            addRelationLayerId = null;
                            addRelationMode = false;
                        "
                    />
                </b-card-body>
            </b-card>
            <template v-else>
                <template
                    v-if="
                        relationshipType ==
                            RELATIONSHIP_TYPES.ThemeLayer2Table &&
                        !hasTableResults
                    "
                >
                    {{ $t('tableResults.firstCreate') }}
                </template>
                <template v-else>
                    <b-link
                        :id="`addRelation`"
                        :name="`addRelation`"
                        v-if="!addRelationMode"
                        @click="$_addRelation"
                    >
                        <em class="fa fa-plus pr-1"></em
                        >{{ $t('relations.addRelation') }}
                    </b-link>
                    <b-card no-body v-if="addRelationMode">
                        <b-card-body>
                            <x-select
                                v-if="layerOptions"
                                :id="'relations-layer-selection'"
                                :name="'relations-layer-selection'"
                                :text="targetLabel"
                                :options="layerOptions"
                                @selected="$_onAddLayerSelected"
                            />
                        </b-card-body>
                    </b-card>
                </template>
            </template>
        </template>
    </div>
</template>

<script>
    import { OWS_TYPE, RELATIONSHIP_TYPES } from '../../../constants';
    import { arraySortAscendingFunction } from '../../../services/utilities';
    import modalMixin from '../../../components/Mixins/ModalMixin.vue';
    import Relation from './Relation.vue';
    import XSelect from '../../BaseComponents/x-select.vue';
    import draggable from 'vuedraggable';

    const filterEmptyItems = (relation) => {
        if (relation.settings.items) {
            const itemsLength = relation.settings.items.length;
            relation.settings.items = relation.settings.items.filter(
                (item) =>
                    item.sourceFieldName !== null &&
                    item.destinationFieldName !== null
            );
            if (itemsLength > relation.settings.items.length) {
                relation.mode = 'edit';
            }
        }
        if (relation.settings.itemsSecondRelation) {
            const itemsSecondLength = relation.settings.itemsSecondRelation.length;
            relation.settings.itemsSecondRelation =
                relation.settings.itemsSecondRelation.filter(
                    (item) =>
                        item.sourceFieldName !== null &&
                        item.destinationFieldName !== null
                );
            if (itemsSecondLength > relation.settings.itemsSecondRelation.length) {
                relation.mode = 'edit';
            }
        }
        return relation;
    };

    export default {
        mixins: [modalMixin],
        props: {
            id: { type: Number, required: true },
            layerId: { type: Number, required: true },
            title: { type: String, required: true },
            targetLabel: { type: String, required: true },
            relationshipType: {
                type: Number,
                required: false,
                default: RELATIONSHIP_TYPES.ThemeLayer2ThemeLayer
            }
        },
        components: {
            'themelayer-relation': Relation,
            'v-draggable': draggable,
            'x-select': XSelect
        },
        data() {
            return {
                RELATIONSHIP_TYPES: RELATIONSHIP_TYPES,
                addRelationMode: false,
                selectableLayers: null,
                addRelationLayerId: null,
                relationList: null,
                accordionState: [],
                tableNames: [],
                newRelationId: -1 // Startpoint for new relations. They get a negative ID value
            };
        },
        created() {
            this.init();
        },
        watch: {
            relationList() {
                this.relationList
                    .filter((relation) => relation.mode !== 'delete')
                    .forEach(function (relation, i) {
                        relation.order = i;
                        relation.mode = 'edit';
                    });
            }
        },
        computed: {
            layerOptions() {
                if (!this.selectableLayers?.map) {
                    return [];
                }
                return this.selectableLayers
                    .map((layer) => ({ text: layer.title, value: layer.id }))
                    .sort(arraySortAscendingFunction('text'));
            },
            hasTableResults() {
                return this.selectableLayers && this.selectableLayers.length > 0;
            }
        },
        methods: {
            async init() {
                if (this.relationshipType === RELATIONSHIP_TYPES.ThemeLayer2Table) {
                    await this.loadTableRelations();
                    await this.$_loadTableSearchResults();
                    await this.$_loadTableNames();
                } else if (
                    this.relationshipType === RELATIONSHIP_TYPES.Table2Table
                ) {
                    await this.loadTableTableRelations();
                    await this.$_loadTableSearchResults();
                    await this.$_loadTableNames();
                } else {
                    await this.loadRelations();
                    await this.$_loadThemeLayers();
                }
            },
            async $_loadTableSearchResults() {
                const tableResults = await this.$store.dispatch('useApiData', {
                    name: 'GetTableSearchResults',
                    parameters: {}
                });
                this.selectableLayers = tableResults?.data;
            },
            async loadTableRelations() {
                const relationsResult = await this.$store.dispatch('useApiData', {
                    name: 'GetThemeLayerTableRelations',
                    parameters: { Id: this.layerId }
                });
                const data = relationsResult?.data ? [...relationsResult.data] : [];
                this.relationList = data.map((relation) => {
                    if (typeof relation.settings === 'string') {
                        return {
                            ...relation,
                            settings: JSON.parse(relation.settings),
                            targetThemeLayerId: relation.toTableSearchResultId
                        };
                    }
                    return relation;
                });
                this.$_collapseAllRelations();
            },

            async loadTableTableRelations() {
                const relationsResult = await this.$store.dispatch('useApiData', {
                    name: 'GetTableTableRelations',
                    parameters: { Id: this.layerId }
                });
                const data = relationsResult?.data ? [...relationsResult.data] : [];
                this.relationList = data.map((relation) => {
                    if (typeof relation.settings === 'string') {
                        return {
                            ...relation,
                            settings: JSON.parse(relation.settings),
                            targetThemeLayerId: relation.toTableSearchResultId
                        };
                    }
                    return relation;
                });
                this.$_collapseAllRelations();
            },
            async $_loadTableNames() {
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetTableNames',
                    parameters: { geometrytables: false }
                });
                this.tableNames = result?.data?.map((item) => {
                    return { layerName: item };
                });
            },
            async $_loadThemeLayers() {
                const layersResult = await this.$store.dispatch('useApiData', {
                    name: 'GetThemeLayersOverview'
                });
                this.selectableLayers =
                    layersResult?.data.filter(
                        ({ serviceType, id }) =>
                            serviceType === OWS_TYPE.WFS && id !== this.layerId
                    ) || [];
            },
            async loadRelations() {
                const relationsResult = await this.$store.dispatch('useApiData', {
                    name: 'GetThemeLayerRelations',
                    parameters: {
                        themeId: 0,
                        themeLayerId: this.layerId,
                        dashboard: true
                    }
                });

                const data = relationsResult?.data ? [...relationsResult.data] : [];
                this.relationList = data.map((relation) => {
                    if (typeof relation.settings === 'string') {
                        return { ...relation };
                    }
                    return relation;
                });
                this.$_collapseAllRelations();
            },
            $_updateRelationData(relationData) {
                if (!relationData.id) {
                    // A new relation has no ID value.
                    // Give it a negative value.
                    relationData.id = this.newRelationId--;
                    this.addRelationLayerId = null;
                    this.relationList.push(relationData);
                    this.accordionState.push(false);
                } else {
                    const relationEditData = { ...relationData, mode: 'edit' };
                    this.relationList = this.relationList.map((relation) => ({
                        ...(relation.id === relationData.id
                            ? relationEditData
                            : relation)
                    }));
                }
                this.addRelationMode = false;
            },
            async saveRelations() {
                this.$_collapseAllRelations();
                return Promise.all(
                    this.relationList
                        // do not save new relations that have been marked to be removed
                        // New (unsaved) relations have a negative ID value
                        .filter(
                            (relation) =>
                                !(relation.id < 0 && relation.mode === 'delete')
                        )
                        // do not save items with null values in sourceFieldName/destinationFieldName
                        .map(filterEmptyItems)
                        // do not save unchanged relations
                        .filter((relation) => !(relation.id > 0 && !relation.mode))
                        .map((relation) => {
                            if (relation.id < 0) {
                                // Remove ID value before saving to database.
                                delete relation.id;
                                return this.$store.dispatch('useApiData', {
                                    name: 'CreateThemeLayerRelation',
                                    parameters: {
                                        themeLayerId: this.layerId
                                    },
                                    data: relation
                                });
                            }
                            if (relation.mode === 'edit') {
                                return this.$store.dispatch('useApiData', {
                                    name: 'UpdateThemeLayerRelation',
                                    parameters: {
                                        themeLayerId: this.layerId,
                                        relationId: relation.id
                                    },
                                    data: {
                                        ...relation,
                                        mode: undefined
                                    }
                                });
                            }
                            if (relation.mode === 'delete') {
                                return this.$store.dispatch('useApiData', {
                                    name: 'DeleteThemeLayerRelation',
                                    parameters: {
                                        themeLayerId: this.layerId,
                                        relationId: relation.id
                                    },
                                    data: {
                                        ...relation,
                                        mode: undefined
                                    }
                                });
                            }
                            return Promise.resolve();
                        })
                );
            },
            async saveTableRelations() {
                this.$_collapseAllRelations();

                return Promise.all(
                    this.relationList
                        // do not save new relations that have been marked to be removed
                        // New (unsaved) relations have a negative ID value
                        .filter(
                            (relation) =>
                                !(relation.id < 0 && relation.mode === 'delete')
                        )
                        // do not save items with null values in sourceFieldName/destinationFieldName
                        .map(filterEmptyItems)
                        // do not save unchanged relations
                        .filter((relation) => !(relation.id > 0 && !relation.mode))
                        .map((relation) => {
                            relation.toTableSearchResultId =
                                relation.targetThemeLayerId;
                            relation.fromThemeLayerId = this.layerId;
                            relation.settings = JSON.stringify(relation.settings);

                            if (relation.id < 0) {
                                relation.id = 0;
                                return this.$store.dispatch('useApiData', {
                                    name: 'CreateThemeLayerTableRelation',
                                    data: relation
                                });
                            }
                            if (relation.mode === 'edit') {
                                return this.$store.dispatch('useApiData', {
                                    name: 'UpdateThemeLayerTableRelation',
                                    data: relation
                                });
                            }
                            if (relation.mode === 'delete') {
                                return this.$store.dispatch('useApiData', {
                                    name: 'DeleteThemeLayerTableRelation',
                                    parameters: { Id: relation.id },
                                    data: relation
                                });
                            }
                            return Promise.resolve();
                        })
                );
            },
            async saveTableTableRelations() {
                this.$_collapseAllRelations();

                return Promise.all(
                    this.relationList
                        // do not save new relations that have been marked to be removed
                        // New (unsaved) relations have a negative ID value
                        .filter(
                            (relation) =>
                                !(relation.id < 0 && relation.mode === 'delete')
                        )
                        // do not save items with null values in sourceFieldName/destinationFieldName
                        .map(filterEmptyItems)
                        // do not save unchanged relations
                        .filter((relation) => !(relation.id > 0 && !relation.mode))
                        .map((relation) => {
                            relation.toTableSearchResultId =
                                relation.targetThemeLayerId;
                            relation.fromTableSearchResultId = this.layerId;
                            relation.settings = JSON.stringify(relation.settings);

                            if (relation.id < 0) {
                                relation.id = 0;
                                return this.$store.dispatch('useApiData', {
                                    name: 'CreateTableTableRelation',
                                    data: relation
                                });
                            }
                            if (relation.mode === 'edit') {
                                return this.$store.dispatch('useApiData', {
                                    name: 'UpdateTableTableRelation',
                                    data: relation
                                });
                            }
                            if (relation.mode === 'delete') {
                                return this.$store.dispatch('useApiData', {
                                    name: 'DeleteTableTableRelation',
                                    parameters: { Id: relation.id },
                                    data: relation
                                });
                            }
                            return Promise.resolve();
                        })
                );
            },
            $_onAddLayerSelected(layerId) {
                this.addRelationLayerId = layerId;
            },
            $_addRelation() {
                this.addRelationMode = true;
                this.$_collapseAllRelations();
            },
            $_selectRelation(index) {
                const newVal = !this.accordionState[index];
                this.accordionState = this.relationList.map((format) => false);
                this.accordionState[index] = newVal;
            },
            $_onRemoveRelation(deleteindex) {
                const data = { message: this.$t('relations.deleteConfirmation') };
                this.msgBoxConfirm(data).then((confirmed) => {
                    if (confirmed) {
                        this.relationList = this.relationList.map(
                            (relation, index) => ({
                                ...relation,
                                mode:
                                    index === deleteindex ? 'delete' : relation.mode
                            })
                        );
                    }
                });
            },
            $_collapseAllRelations() {
                this.accordionState = this.relationList.map(() => false);
            },
            validateRelations() {
                // Check if relations contains attributes
                return this.relationList
                    .map((relation) => {
                        if (
                            relation.mode !== 'delete' &&
                            (relation.settings?.items?.length === 0 ||
                                relation.name.trim() === '')
                        ) {
                            return `- ${relation.name}`;
                        }
                        return undefined;
                    })
                    .filter((relation) => {
                        return relation !== undefined;
                    });
            }
        }
    };
</script>
<style scoped>
.fa-arrows-alt:hover {
    cursor: move;
}
</style>
